/**
 * The saved quiz and the saved case: the two localStorage keys the club funnel keeps, and the one
 * rule for how long they last.
 *
 * Two files read these. The funnel (assets/wine-club-quiz.js, assets/club-result.js) writes them;
 * the sitewide pick-up (assets/club-pickup.js) reads them to offer a visitor their case back. Both
 * import this module, so the key names and the fourteen days exist once, here, and cannot drift.
 *
 * The saved case is `{ v: 1, savedAt, answersKey, size, cadence, lines: [{ variantId, handle }] }`.
 * `answersKey` is assets/club-case.js `answersKey(answers)` of the quiz's saved answers the case
 * was built from, with no pace argument, so anyone holding the quiz key can work out the same
 * string and tell a case built from the answers saved now from one built from older answers.
 *
 * Anything older than MAX_AGE_MS is treated as absent and removed when read. A saved value with no
 * `savedAt` has an unknown age and counts as old. Storage that throws (a private window, blocked
 * site data) reads as nothing saved, and every write quietly does nothing.
 *
 * This module may not import anything: Node has no import map, so '@theme/...' does not resolve,
 * and scripts/tests/club-case-store.test.mjs loads it directly.
 */

export const QUIZ_KEY = 'givino:wine-club-quiz';
export const CASE_KEY = 'givino:wine-club-case';

/** Fourteen days, in milliseconds. The only place the number lives. */
export const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** @returns {Storage | null} */
function local() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * True when a value saved at `savedAt` is still within MAX_AGE_MS of `now`.
 * @param {unknown} savedAt epoch milliseconds
 * @param {number} [now]
 */
export function isFresh(savedAt, now = Date.now()) {
  if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return false;
  return now - savedAt <= MAX_AGE_MS;
}

/**
 * The saved case, or null when there is none, it is malformed, or it is older than MAX_AGE_MS. An
 * old or malformed one is removed.
 * @param {{ storage?: Storage | null, now?: number }} [options]
 * @returns {{ v: 1, savedAt: number, answersKey: string, size: number, cadence: string,
 *   lines: { variantId: number, handle: string }[] } | null}
 */
export function readCase({ storage = local(), now = Date.now() } = {}) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(CASE_KEY);
    if (raw === null) return null;
    const saved = JSON.parse(raw);
    const lines = Array.isArray(saved?.lines)
      ? saved.lines.filter((line) => Number.isFinite(Number(line?.variantId)))
      : [];
    if (saved?.v !== 1 || !isFresh(saved.savedAt, now) || !lines.length) {
      storage.removeItem(CASE_KEY);
      return null;
    }
    return {
      v: 1,
      savedAt: saved.savedAt,
      answersKey: String(saved.answersKey ?? ''),
      size: Number(saved.size),
      cadence: String(saved.cadence ?? ''),
      lines: lines.map((line) => ({ variantId: Number(line.variantId), handle: String(line.handle ?? '') })),
    };
  } catch {
    return null;
  }
}

/**
 * Save the case, stamped with `now`.
 * @param {{ answersKey: string, size: number, cadence: string,
 *   lines: { variantId: number, handle: string }[] }} data
 * @param {{ storage?: Storage | null, now?: number }} [options]
 * @returns {boolean} true when it was written
 */
export function writeCase(data, { storage = local(), now = Date.now() } = {}) {
  if (!storage) return false;
  try {
    const record = {
      v: 1,
      savedAt: now,
      answersKey: String(data.answersKey ?? ''),
      size: Number(data.size),
      cadence: String(data.cadence ?? ''),
      lines: (data.lines ?? []).map((line) => ({ variantId: Number(line.variantId), handle: String(line.handle ?? '') })),
    };
    storage.setItem(CASE_KEY, JSON.stringify(record));
    // The case is only of use with the answers it was built from, so keeping one keeps the other:
    // a case swapped on day thirteen must not outlive answers that were last touched on day one.
    const quiz = JSON.parse(storage.getItem(QUIZ_KEY) ?? 'null');
    if (quiz?.answers && typeof quiz.answers === 'object') {
      storage.setItem(QUIZ_KEY, JSON.stringify({ ...quiz, savedAt: now }));
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Forget both: the answers and the case. Called once the case is in the cart, so a visitor who has
 * bought is not asked to pick it up again.
 * @param {{ storage?: Storage | null }} [options]
 */
export function clearAll({ storage = local() } = {}) {
  if (!storage) return;
  try {
    storage.removeItem(QUIZ_KEY);
    storage.removeItem(CASE_KEY);
  } catch {
    // Blocked storage has nothing to forget.
  }
}
